import React, { useCallback, useMemo } from "react";
import { Row } from "@tanstack/react-table";
import { DataTable } from "ui-components/data-table";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Loading from "ui-components/loading/loading";
import { PlanningFreightProbill } from "app/data/linehaul-outbound-planning/models";
import { useCombinedPlanningFreightList } from "../hooks/useCombinedPlanningFreightList";
import { useDrawerTableContainerHeight } from "../hooks/useDrawerTableContainerHeight";
import {
  getOutboundPlanningShipmentColumns,
  getRowCanExpandFreight,
  OUTBOUND_PLANNING_ALL_SHIPMENT_COLUMN_OPTIONS,
  OUTBOUND_PLANNING_ALL_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT,
} from "../columns/outboundPlanningShipmentColumns";
import PlanningFreightListTotalsBar, {
  PLANNING_FREIGHT_TOTALS_METRICS,
} from "./planningFreightListTotalsBar";
import {
  getOutboundPlanningItemColumns,
  OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS,
  OUTBOUND_PLANNING_ITEM_COLUMNS_HIDDEN_BY_DEFAULT,
} from "../columns/outboundPlanningItemColumns";
import { useDataTableColumnPreferences } from "hooks/useDataTableColumnPreferences";
import "../outboundPlanningList.scss";

const MAIN_ROW_HEIGHT = 44;
const ITEM_ROW_HEIGHT = 36;

const COMBINED_ITEM_COLUMNS_HIDDEN_BY_DEFAULT = [
  ...OUTBOUND_PLANNING_ITEM_COLUMNS_HIDDEN_BY_DEFAULT,
  "itemLocation",
  "lastChanged",
  "lastChangeUser",
  "updatedAt",
  "addedAt",
];

interface CombinedFreightListPanelProps {
  originTerminalNumber: number;
  destinationTerminalNumbers: number[];
  fetchId: string;
  onPickupNumberClick?: (pickupNumber: string) => void;
}

const CombinedFreightListPanel: React.FC<CombinedFreightListPanelProps> = ({
  originTerminalNumber,
  destinationTerminalNumbers,
  fetchId,
  onPickupNumberClick,
}) => {
  const { containerRef, tableHeight } = useDrawerTableContainerHeight(24);
  const { data, isLoading, isError } = useCombinedPlanningFreightList(
    originTerminalNumber,
    destinationTerminalNumbers,
    fetchId,
    true
  );

  const shipmentColumnPrefs = useDataTableColumnPreferences({
    instanceKey: "outbound-planning-all-shipments",
    columnOptions: OUTBOUND_PLANNING_ALL_SHIPMENT_COLUMN_OPTIONS,
    hiddenByDefault: OUTBOUND_PLANNING_ALL_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT,
  });
  const itemColumnPrefs = useDataTableColumnPreferences({
    instanceKey: "outbound-planning-all-items",
    columnOptions: OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS,
    hiddenByDefault: COMBINED_ITEM_COLUMNS_HIDDEN_BY_DEFAULT,
  });

  const shipmentColumns = useMemo(
    () =>
      getOutboundPlanningShipmentColumns({
        category: "all",
        originTerminalNumber,
        onPickupNumberClick,
      }),
    [originTerminalNumber, onPickupNumberClick]
  );

  const itemColumns = useMemo(() => getOutboundPlanningItemColumns(), []);

  const renderSubComponent = useCallback(
    (row: Row<PlanningFreightProbill>) => (
      <div className="outbound-planning-freight-list-panel__sub-table">
        <DataTable
          columns={itemColumns}
          data={row.original.items ?? []}
          {...itemColumnPrefs}
          showColumnSelector
          columnSelectorOptions={OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS}
          enableColumnResize
          enableColumnReorder
          enableRowSelection={false}
          responsive
          autoHeight={false}
          shrinkToContent
          rowHeight={ITEM_ROW_HEIGHT}
          noResultsText="No items for this shipment"
        />
      </div>
    ),
    [itemColumns, itemColumnPrefs]
  );

  return (
    <div className="outbound-planning-freight-list-panel">
      <div ref={containerRef} className="outbound-planning-freight-list-panel__table">
        {isError ? (
          <XGSErrorMessage>Error loading shipments. Please try again.</XGSErrorMessage>
        ) : isLoading ? (
          <div className="outbound-planning-freight-list-panel__loading">
            <Loading isLoading />
          </div>
        ) : !data || data.length === 0 ? (
          <div className="outbound-planning-freight-list-panel__empty">No shipments found.</div>
        ) : (
          <DataTable
            columns={shipmentColumns}
            data={data}
            isLoading={false}
            {...shipmentColumnPrefs}
            showColumnSelector
            columnSelectorOptions={OUTBOUND_PLANNING_ALL_SHIPMENT_COLUMN_OPTIONS}
            enableColumnResize
            enableColumnReorder
            enableRowSelection={false}
            responsive
            sorting
            enableMultiSort
            filters
            renderSubComponent={renderSubComponent}
            getRowCanExpand={getRowCanExpandFreight}
            stickyLeadingColumnId="expand"
            autoHeight={false}
            listHeightOverride={tableHeight ?? 400}
            rowHeight={MAIN_ROW_HEIGHT}
            stickyHeaders
            renderTableFooter={({ rows }) => (
              <PlanningFreightListTotalsBar
                rows={rows}
                rowHeight={MAIN_ROW_HEIGHT}
                metrics={PLANNING_FREIGHT_TOTALS_METRICS.map((metric) =>
                  metric.id === "probillCount"
                    ? { ...metric, label: "Shipments", tooltip: "Count of all shipments in the list" }
                    : metric
                )}
              />
            )}
            customHeaderRowClass="outbound-planning-shipment-table__header-row"
          />
        )}
      </div>
    </div>
  );
};

export default CombinedFreightListPanel;
